import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { bibliothequeService, Marabout, Livre, Sourate } from '../services/bibliothequeService';

export const Bibliotheque: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'marabouts' | 'livres' | 'coran'>('marabouts');
  const [marabouts, setMarabouts] = useState<Marabout[]>([]);
  const [livres, setLivres] = useState<Livre[]>([]);
  const [sourates, setSourates] = useState<Sourate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // États de pagination séparés pour chaque onglet
  const [currentPageMarabouts, setCurrentPageMarabouts] = useState(1);
  const [totalPagesMarabouts, setTotalPagesMarabouts] = useState(1);
  const [totalCountMarabouts, setTotalCountMarabouts] = useState(0);
  
  const [currentPageLivres, setCurrentPageLivres] = useState(1);
  const [totalPagesLivres, setTotalPagesLivres] = useState(1);
  const [totalCountLivres, setTotalCountLivres] = useState(0);
  
  const [currentPageCoran, setCurrentPageCoran] = useState(1);
  const [totalPagesCoran, setTotalPagesCoran] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const [filters, setFilters] = useState({
    categorie: '',
    langue: '',
    auteur: '',
  });

  // Reset pagination quand on change d'onglet ou de filtre
  useEffect(() => {
    if (activeTab === 'marabouts') {
      setCurrentPageMarabouts(1);
      fetchMarabouts(1);
    }
    if (activeTab === 'livres') {
      setCurrentPageLivres(1);
      fetchLivres(1);
    }
  }, [activeTab, filters.categorie, filters.langue, filters.auteur]);

  // Reset pagination quand la recherche change
  useEffect(() => {
    if (activeTab === 'marabouts') {
      setCurrentPageMarabouts(1);
      fetchMarabouts(1);
    }
    if (activeTab === 'livres') {
      setCurrentPageLivres(1);
      fetchLivres(1);
    }
  }, [searchTerm]);

  const fetchMarabouts = async (page: number = 1) => {
    setLoading(true);
    try {
      const data = await bibliothequeService.getMarabouts(page, 6, { search: searchTerm });
      setMarabouts(data.results);
      setTotalCountMarabouts(data.count);
      setTotalPagesMarabouts(Math.ceil(data.count / 6));
    } catch (error) {
      console.error('Erreur:', error);
      setMarabouts([]);
      setTotalCountMarabouts(0);
      setTotalPagesMarabouts(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchLivres = async (page: number = 1) => {
    setLoading(true);
    try {
      const filterParams: any = {};
      if (filters.categorie) filterParams.categorie = filters.categorie;
      if (filters.langue) filterParams.langue = filters.langue;
      if (filters.auteur) filterParams.auteur = filters.auteur;
      if (searchTerm) filterParams.search = searchTerm;
      const data = await bibliothequeService.getLivres(page, 6, filterParams);
      setLivres(data.results);
      setTotalCountLivres(data.count);
      setTotalPagesLivres(Math.ceil(data.count / 6));
    } catch (error) {
      console.error('Erreur:', error);
      setLivres([]);
      setTotalCountLivres(0);
      setTotalPagesLivres(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchSourates = async (page: number = 1) => {
    setLoading(true);
    try {
      const data = await bibliothequeService.getSourates(page, 8);
      setSourates(data.results);
      setTotalCount(data.count);
      // Calculer le nombre total de pages (8 sourates par page)
      setTotalPagesCoran(Math.ceil(data.count / 8));
    } catch (error) {
      console.error('Erreur:', error);
      setSourates([]);
      setTotalPagesCoran(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'coran') {
      fetchSourates(currentPageCoran);
    }
  }, [currentPageCoran, activeTab]);

  const handleSyncSourates = async () => {
    if (window.confirm('Voulez-vous synchroniser toutes les 114 sourates depuis l\'API Al-Quran Cloud ?')) {
      handleSyncSouratesConfirm();
    }
  };

  const handleSyncSouratesConfirm = async () => {
    try {
      setLoading(true);
      const result = await bibliothequeService.syncSouratesFromAPI();
      alert(result.message);
      // Recharger les sourates après synchronisation (première page)
      setCurrentPageCoran(1);
      await fetchSourates(1);
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(error.response?.data?.error || 'Erreur lors de la synchronisation');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'marabouts' as const, label: 'Marabouts', icon: '', color: 'from-green-500 to-green-600' },
    { id: 'livres' as const, label: 'Livres', icon: '', color: 'from-green-500 to-green-600' },
    { id: 'coran' as const, label: 'Coran', icon: '', color: 'from-green-500 to-green-600' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-green-50/30 to-green-50/50 py-12 sm:py-16 md:py-20 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8 sm:mb-12">
            <div className="relative inline-block bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-3xl px-8 py-8 shadow-2xl overflow-hidden group mb-8">
              {/* Effet de brillance animé */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              {/* Contenu */}
              <div className="relative z-10">
                <h1 className="text-5xl sm:text-6xl font-bold text-white mb-4 drop-shadow-lg">
                  Bibliothèque
                </h1>
                <p className="text-green-50 text-xl font-medium">
                  Découvrez nos marabouts, livres et le Coran complet
                </p>
              </div>
            </div>

            {/* Tabs modernes */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-2 shadow-lg border border-green-100/50 max-w-2xl mx-auto inline-flex mb-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSearchTerm('');
                    setFilters({ categorie: '', langue: '', auteur: '' });
                    setCurrentPageMarabouts(1);
                    setCurrentPageLivres(1);
                    setCurrentPageCoran(1);
                  }}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform ${
                    activeTab === tab.id
                      ? `bg-gradient-to-r ${tab.color} text-white shadow-lg scale-105`
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Filtres et recherche pour Marabouts */}
            {activeTab === 'marabouts' && (
              <div className="max-w-2xl mx-auto mt-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50">
                  <input
                    type="text"
                    placeholder="🔍 Rechercher un marabout..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  />
                </div>
              </div>
            )}

            {/* Filtres et recherche pour Livres */}
            {activeTab === 'livres' && (
              <div className="max-w-4xl mx-auto mt-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-green-100/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <input
                      type="text"
                      placeholder="🔍 Rechercher un livre..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white lg:col-span-2"
                    />
                    <select
                      value={filters.categorie}
                      onChange={(e) => setFilters({ ...filters, categorie: e.target.value })}
                      className="px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                    >
                      <option value="">Toutes les catégories</option>
                      <option value="tafsir">📖 Tafsir</option>
                      <option value="fiqh">⚖️ Fiqh</option>
                      <option value="hadith">📜 Hadith</option>
                      <option value="histoire">📚 Histoire</option>
                      <option value="biographie">👤 Biographie</option>
                    </select>
                    <select
                      value={filters.langue}
                      onChange={(e) => setFilters({ ...filters, langue: e.target.value })}
                      className="px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                    >
                      <option value="">Toutes les langues</option>
                      <option value="français">🇫🇷 Français</option>
                      <option value="arabe">🇸🇦 Arabe</option>
                      <option value="wolof">🇸🇳 Wolof</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contenu */}
      <section className="relative bg-gradient-to-br from-green-50/30 via-white to-green-50/50 py-6 sm:py-8 md:py-10 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-green-200/15 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-green-100/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Filtres mobiles - visible seulement sur mobile/tablet */}
          <div className="lg:hidden mb-4">
            {/* Filtres pour Marabouts */}
            {activeTab === 'marabouts' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50">
                <input
                  type="text"
                  placeholder="🔍 Rechercher un marabout..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                />
              </div>
            )}

            {/* Filtres pour Livres */}
            {activeTab === 'livres' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="🔍 Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                  />
                  <select
                    value={filters.categorie}
                    onChange={(e) => setFilters({ ...filters, categorie: e.target.value })}
                    className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                  >
                    <option value="">Toutes catégories</option>
                    <option value="religion">📖 Religion</option>
                    <option value="histoire">📜 Histoire</option>
                    <option value="science">🔬 Science</option>
                    <option value="litterature">📚 Littérature</option>
                  </select>
                  <select
                    value={filters.langue}
                    onChange={(e) => setFilters({ ...filters, langue: e.target.value })}
                    className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                  >
                    <option value="">Toutes langues</option>
                    <option value="fr">🇫🇷 Français</option>
                    <option value="ar">🇸🇦 Arabe</option>
                    <option value="wo">🇸🇳 Wolof</option>
                  </select>
                </div>
                <input
                  type="text"
                  placeholder="👤 Rechercher par auteur..."
                  value={filters.auteur}
                  onChange={(e) => setFilters({ ...filters, auteur: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-sm"
                />
              </div>
            )}

            {/* Filtres pour Coran */}
            {activeTab === 'coran' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <select
                    value={filters.langue}
                    onChange={(e) => setFilters({ ...filters, langue: e.target.value })}
                    className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                  >
                    <option value="">Toutes langues</option>
                    <option value="fr">🇫🇷 Français</option>
                    <option value="ar">🇸🇦 Arabe</option>
                    <option value="wo">🇸🇳 Wolof</option>
                  </select>
                  <input
                    type="text"
                    placeholder="🔍 Rechercher une sourate..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Layout latéral avec filtres et contenu */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
            {/* Sidebar filtres - seulement sur desktop */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-amber-100/50 sticky top-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">🔍 Filtres</h3>
                
                {/* Filtres pour Marabouts */}
                {activeTab === 'marabouts' && (
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="🔍 Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all bg-white text-sm"
                    />
                  </div>
                )}

                {/* Filtres pour Livres */}
                {activeTab === 'livres' && (
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="🔍 Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-sm"
                    />
                    <select
                      value={filters.categorie}
                      onChange={(e) => setFilters({ ...filters, categorie: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-sm"
                    >
                      <option value="">Toutes catégories</option>
                      <option value="religion">📖 Religion</option>
                      <option value="histoire">📜 Histoire</option>
                      <option value="science">🔬 Science</option>
                      <option value="litterature">📚 Littérature</option>
                    </select>
                    <select
                      value={filters.langue}
                      onChange={(e) => setFilters({ ...filters, langue: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-sm"
                    >
                      <option value="">Toutes langues</option>
                      <option value="fr">🇫🇷 Français</option>
                      <option value="ar">🇸🇦 Arabe</option>
                      <option value="wo">🇸🇳 Wolof</option>
                    </select>
                    <input
                      type="text"
                      placeholder="👤 Rechercher auteur..."
                      value={filters.auteur}
                      onChange={(e) => setFilters({ ...filters, auteur: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-sm"
                    />
                  </div>
                )}

                {/* Filtres pour Coran */}
                {activeTab === 'coran' && (
                  <div className="space-y-4">
                    <select
                      value={filters.langue}
                      onChange={(e) => setFilters({ ...filters, langue: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                    >
                      <option value="">Toutes langues</option>
                      <option value="fr">🇫🇷 Français</option>
                      <option value="ar">🇸🇦 Arabe</option>
                      <option value="wo">🇸🇳 Wolof</option>
                    </select>
                    <input
                      type="text"
                      placeholder="🔍 Rechercher sourate..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Contenu principal */}
            <div className="lg:col-span-3">
          {loading ? (
            <div className="flex justify-center items-center py-32">
              <div className="relative">
                <div className={`animate-spin rounded-full h-20 w-20 border-4 ${
                  activeTab === 'marabouts' ? 'border-amber-200 border-t-amber-700' :
                  activeTab === 'livres' ? 'border-blue-200 border-t-blue-700' :
                  'border-green-200 border-t-green-700'
                }`}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl">{tabs.find(t => t.id === activeTab)?.icon}</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Marabouts */}
              {activeTab === 'marabouts' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {marabouts.length > 0 ? (
                    marabouts.map((marabout, index) => (
                      <Link
                        key={marabout.id}
                        to={`/bibliotheque/marabouts/${marabout.id}`}
                        className="group animate-slide-up"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border border-amber-100/50 h-full flex flex-col">
                          {/* Header avec photo ou gradient */}
                          <div className={`relative h-48 ${marabout.photo ? '' : 'bg-gradient-to-br from-amber-500 to-amber-600'} overflow-hidden`}>
                            {marabout.photo ? (
                              <img
                                src={marabout.photo}
                                alt={marabout.nom_complet}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-7xl opacity-50">👳</span>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                            {marabout.nombre_livres > 0 && (
                              <div className="absolute top-4 right-4">
                                <span className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm font-bold text-gray-900 shadow-lg">
                                  📖 {marabout.nombre_livres} livre{marabout.nombre_livres > 1 ? 's' : ''}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Contenu */}
                          <div className="p-6 flex-1 flex flex-col">
                            <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-amber-700 transition-colors">
                              {marabout.nom_complet}
                            </h3>
                            
                            <div className="space-y-2 mb-4 flex-1">
                              {marabout.nom_arabe && (
                                <p className="text-gray-700 font-medium text-lg text-right" dir="rtl">
                                  {marabout.nom_arabe}
                                </p>
                              )}
                              <div className="space-y-1">
                                {marabout.periode && (
                                  <p className="text-gray-600 text-sm">📅 {marabout.periode}</p>
                                )}
                                {marabout.lieu_naissance && (
                                  <p className="text-gray-600 text-sm">📍 {marabout.lieu_naissance}</p>
                                )}
                                {marabout.nombre_livres > 0 && (
                                  <p className="text-amber-700 text-sm font-semibold">📚 {marabout.nombre_livres} livre{marabout.nombre_livres > 1 ? 's' : ''}</p>
                                )}
                              </div>
                            </div>

                            <span className="text-amber-700 font-semibold text-sm group-hover:translate-x-2 transition-transform inline-flex items-center space-x-1 mt-auto">
                              <span>Voir biographie</span>
                              <span>→</span>
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-32">
                      <div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-amber-100/50">
                        <div className="text-8xl mb-6">👳</div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">Aucun marabout trouvé</h3>
                      </div>
                    </div>
                  )}
                  
                  {/* Pagination Marabouts */}
                  {totalPagesMarabouts > 1 && (
                    <div className="w-full flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-2 sm:space-x-2 mt-8 px-4">
                      <button
                        onClick={() => {
                          const newPage = Math.max(1, currentPageMarabouts - 1);
                          setCurrentPageMarabouts(newPage);
                          fetchMarabouts(newPage);
                        }}
                        disabled={currentPageMarabouts === 1 || loading}
                        className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        ← Précédent
                      </button>
                      <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-2 sm:pb-0 max-w-full">
                        {Array.from({ length: Math.min(5, totalPagesMarabouts) }, (_, i) => {
                          let pageNum: number;
                          if (totalPagesMarabouts <= 5) {
                            pageNum = i + 1;
                          } else if (currentPageMarabouts <= 3) {
                            pageNum = i + 1;
                          } else if (currentPageMarabouts >= totalPagesMarabouts - 2) {
                            pageNum = totalPagesMarabouts - 4 + i;
                          } else {
                            pageNum = currentPageMarabouts - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => {
                                setCurrentPageMarabouts(pageNum);
                                fetchMarabouts(pageNum);
                              }}
                              disabled={loading}
                              className={`px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                                currentPageMarabouts === pageNum
                                  ? 'bg-amber-600 text-white shadow-lg scale-110'
                                  : 'bg-white text-gray-700 hover:bg-amber-50 border border-amber-200'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => {
                          const newPage = Math.min(totalPagesMarabouts, currentPageMarabouts + 1);
                          setCurrentPageMarabouts(newPage);
                          fetchMarabouts(newPage);
                        }}
                        disabled={currentPageMarabouts === totalPagesMarabouts || loading}
                        className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        Suivant →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Livres */}
              {activeTab === 'livres' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {livres.length > 0 ? (
                    livres.map((livre, index) => (
                      <div
                        key={livre.id}
                        className="group animate-slide-up bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border border-blue-100/50 h-full flex flex-col"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        {/* Header avec couverture ou gradient */}
                        <div className={`relative h-48 ${livre.couverture ? '' : 'bg-gradient-to-br from-blue-500 to-blue-600'} overflow-hidden`}>
                          {livre.couverture ? (
                            <img
                              src={livre.couverture}
                              alt={livre.titre}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-7xl opacity-50">📚</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                          {livre.categorie && (
                            <div className="absolute top-4 left-4">
                              <span className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm font-bold text-gray-900 shadow-lg">
                                📚 {livre.categorie}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Contenu */}
                        <div className="p-6 flex-1 flex flex-col">
                          <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors line-clamp-2">
                            {livre.titre}
                          </h3>
                          
                          <div className="space-y-2 mb-4 flex-1">
                            <p className="text-gray-700 font-medium">
                              ✍️ {livre.auteur?.nom_complet || livre.auteur_nom || livre.auteur_autre || 'Auteur inconnu'}
                            </p>
                            <p className="text-gray-600 text-sm">🌐 {livre.langue}</p>
                            {livre.categorie && (
                              <p className="text-gray-600 text-sm">
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                                  {livre.categorie}
                                </span>
                              </p>
                            )}
                            {livre.nombre_pages && (
                              <p className="text-gray-600 text-sm">📄 {livre.nombre_pages} pages</p>
                            )}
                            {livre.description && (
                              <p className="text-gray-600 text-sm line-clamp-3 mt-2">{livre.description}</p>
                            )}
                          </div>

                          {livre.fichier_pdf && (
                            <a
                              href={livre.fichier_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center justify-center space-x-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>📥</span>
                              <span>Télécharger PDF</span>
                            </a>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-32">
                      <div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-blue-100/50">
                        <div className="text-8xl mb-6">📚</div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">Aucun livre trouvé</h3>
                      </div>
                    </div>
                  )}
                  
                  {/* Pagination Livres */}
                  {totalPagesLivres > 1 && (
                    <div className="w-full flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-2 sm:space-x-2 mt-8 px-4">
                      <button
                        onClick={() => {
                          const newPage = Math.max(1, currentPageLivres - 1);
                          setCurrentPageLivres(newPage);
                          fetchLivres(newPage);
                        }}
                        disabled={currentPageLivres === 1 || loading}
                        className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        ← Précédent
                      </button>
                      <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-2 sm:pb-0 max-w-full">
                        {Array.from({ length: Math.min(5, totalPagesLivres) }, (_, i) => {
                          let pageNum: number;
                          if (totalPagesLivres <= 5) {
                            pageNum = i + 1;
                          } else if (currentPageLivres <= 3) {
                            pageNum = i + 1;
                          } else if (currentPageLivres >= totalPagesLivres - 2) {
                            pageNum = totalPagesLivres - 4 + i;
                          } else {
                            pageNum = currentPageLivres - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => {
                                setCurrentPageLivres(pageNum);
                                fetchLivres(pageNum);
                              }}
                              disabled={loading}
                              className={`px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                                currentPageLivres === pageNum
                                  ? 'bg-blue-600 text-white shadow-lg scale-110'
                                  : 'bg-white text-gray-700 hover:bg-blue-50 border border-blue-200'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => {
                          const newPage = Math.min(totalPagesLivres, currentPageLivres + 1);
                          setCurrentPageLivres(newPage);
                          fetchLivres(newPage);
                        }}
                        disabled={currentPageLivres === totalPagesLivres || loading}
                        className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        Suivant →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Coran avec recherche */}
              {activeTab === 'coran' && (
                <>
                  <div className="mb-6">
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50">
                      <div className="flex justify-center mb-4">
                        <button
                          onClick={handleSyncSourates}
                          disabled={loading}
                          className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                          {loading ? (
                            <>
                              <span className="animate-spin">⏳</span>
                              <span>Synchronisation...</span>
                            </>
                          ) : (
                            <>
                              <span>🔄</span>
                              <span>Synchroniser les 114 sourates</span>
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 text-center">
                        {totalCount === 114 ? (
                          <span className="text-green-600 font-semibold">✅ Toutes les 114 sourates sont disponibles</span>
                        ) : (
                          <span>📊 {totalCount} / 114 sourates disponibles. Cliquez sur "Synchroniser" pour charger toutes les sourates depuis l'API.</span>
                        )}
                        {totalPagesCoran > 1 && (
                          <span className="block mt-2 text-gray-500">
                            Page {currentPageCoran} / {totalPagesCoran}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {sourates.length > 0 ? (
                    sourates
                      .filter((sourate) =>
                        !searchTerm ||
                        sourate.nom_francais.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        sourate.nom_arabe.includes(searchTerm) ||
                        sourate.numero.toString().includes(searchTerm)
                      )
                      .map((sourate, index) => (
                      <Link
                        key={sourate.id}
                        to={`/bibliotheque/coran/${sourate.id}`}
                        className="group animate-slide-up"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border border-green-100/50 h-full flex flex-col">
                          {/* Header avec gradient */}
                          <div className="relative h-32 bg-gradient-to-br from-green-500 to-green-600 overflow-hidden">
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                              <span className="text-4xl mb-2 opacity-80">📿</span>
                              <span className="text-2xl font-bold">{sourate.numero}</span>
                            </div>
                          </div>

                          {/* Contenu */}
                          <div className="p-5 flex-1 flex flex-col">
                            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-700 transition-colors line-clamp-1">
                              {sourate.nom_francais}
                            </h3>
                            
                            <p className="text-gray-700 font-medium text-lg mb-3 text-right" dir="rtl">
                              {sourate.nom_arabe}
                            </p>
                            
                            <div className="space-y-1 mb-4 text-sm text-gray-600">
                              <p>📖 {sourate.nombre_versets} versets</p>
                              <p>📌 {sourate.type_sourate}</p>
                            </div>

                            <span className="text-green-700 font-semibold text-sm group-hover:translate-x-2 transition-transform inline-flex items-center space-x-1 mt-auto">
                              <span>Lire</span>
                              <span>→</span>
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : searchTerm ? (
                    <div className="col-span-full text-center py-32">
                      <div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-green-100/50">
                        <div className="text-8xl mb-6">🔍</div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">Aucune sourate trouvée</h3>
                        <p className="text-gray-600">Essayez avec d'autres mots-clés</p>
                      </div>
                    </div>
                  ) : (
                    <div className="col-span-full text-center py-32">
                      <div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-green-100/50">
                        <div className="text-8xl mb-6">📿</div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">Aucune sourate trouvée</h3>
                      </div>
                    </div>
                    )}
                  </div>
                  
                  {/* Pagination Coran - Centrée */}
                  {totalPagesCoran > 1 && !searchTerm && (
                    <div className="w-full flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-2 sm:space-x-2 mt-8 px-4">
                      <button
                        onClick={() => {
                          const newPage = Math.max(1, currentPageCoran - 1);
                          setCurrentPageCoran(newPage);
                          fetchSourates(newPage);
                        }}
                        disabled={currentPageCoran === 1 || loading}
                        className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        ← Précédent
                      </button>
                      <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-2 sm:pb-0 max-w-full">
                        {Array.from({ length: Math.min(5, totalPagesCoran) }, (_, i) => {
                          let pageNum: number;
                          if (totalPagesCoran <= 5) {
                            pageNum = i + 1;
                          } else if (currentPageCoran <= 3) {
                            pageNum = i + 1;
                          } else if (currentPageCoran >= totalPagesCoran - 2) {
                            pageNum = totalPagesCoran - 4 + i;
                          } else {
                            pageNum = currentPageCoran - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => {
                                setCurrentPageCoran(pageNum);
                                fetchSourates(pageNum);
                              }}
                              disabled={loading}
                              className={`px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                                currentPageCoran === pageNum
                                  ? 'bg-green-600 text-white shadow-lg scale-110'
                                  : 'bg-white text-gray-700 hover:bg-green-50 border border-green-200'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => {
                          const newPage = Math.min(totalPagesCoran, currentPageCoran + 1);
                          setCurrentPageCoran(newPage);
                          fetchSourates(newPage);
                        }}
                        disabled={currentPageCoran === totalPagesCoran || loading}
                        className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        Suivant →
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
